import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Stack } from '@mui/material';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import Circular from 'components/common/CustomCircular';
import ConditionList from 'components/condition-list/ConditionList';
import DetailPanel from 'components/condition-list/DetailPanel';
import { ListPanelItem } from 'components/condition-list/ListPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  SecurityCheckResultDetail,
  SecurityCheckResultListItem,
  getSecurityCheckResult,
  listSecurityCheckResult,
} from 'stores/SecurityCheckStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import SecurityCheckDetail from './components/SecurityCheckDetail';
import SecurityCheckListItem from './components/SecurityCheckListItem';

const SecurityCheckPage: React.FC = () => {
  const { resultId: routeResultId } = useParams<{ resultId?: string }>();
  const [searchParams] = useSearchParams();
  const [isProgress, setProgress] = useState(true);
  const [items, setItems] = useState<SecurityCheckResultListItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<SecurityCheckResultDetail | null>(null);

  // パスパラメータで直接詳細を開くモード
  const isDirectMode = !!routeResultId;

  // クエリパラメータによる初期表示条件
  const queryType = searchParams.get('securityCheckType');
  const queryIncludeCompleted = searchParams.get('includeCompleted') === 'true';

  useEffect(() => {
    if (isDirectMode) {
      // ID指定: その結果のみ取得
      setProgress(true);
      getSecurityCheckResult(routeResultId)
        .then((result) => {
          setSelectedItem(result);
          setProgress(false);
        })
        .catch(() => {
          setProgress(false);
          errorDefault('セキュリティチェック詳細の取得に失敗しました');
        });
    } else {
      fetchList({
        includeCompleted: queryIncludeCompleted,
        securityCheckType:
          queryType === 'MONTHLY' || queryType === 'PC_PATCH' ? queryType : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeResultId, queryType, queryIncludeCompleted]);

  const fetchList = (params: { includeCompleted: boolean; securityCheckType?: string }) => {
    setProgress(true);
    listSecurityCheckResult(
      params as { includeCompleted: boolean; securityCheckType?: 'MONTHLY' | 'PC_PATCH' },
    )
      .then((result) => {
        setItems(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('セキュリティチェック一覧の取得に失敗しました');
      });
  };

  const fetchDetail = (resultId: string) => {
    setProgress(true);
    getSecurityCheckResult(resultId)
      .then((result) => {
        setSelectedItem(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('セキュリティチェック詳細の取得に失敗しました');
      });
  };

  const handleSearch = (filters: Filter[]) => {
    setSelectedItem(null);
    const params: { includeCompleted: boolean; securityCheckType?: string } = {
      includeCompleted: false,
    };
    filters.forEach((f) => {
      if (f.key === 'includeCompleted') {
        params.includeCompleted = true;
      } else if (f.key === 'securityCheckType') {
        params.securityCheckType = f.value.key;
      }
    });
    fetchList(params);
  };

  const handleSelectItem = (item: ListPanelItem) => {
    fetchDetail(item.id);
  };

  const handleExecuted = () => {
    if (isDirectMode) {
      // パスパラメータで開いた場合はタブを閉じる
      window.close();
      return;
    }
    if (selectedItem) {
      fetchDetail(selectedItem.securityCheckResultId);
    }
    // リフレッシュリスト
    fetchList({ includeCompleted: false });
  };

  const conditions: Condition[] = [
    {
      key: 'includeCompleted',
      name: '実施済みを含む',
      type: 'check',
      color: '#81C784',
      value: { key: 'true', name: '実施済みを含む' },
    },
    {
      key: 'securityCheckType',
      name: '種別',
      type: 'select',
      color: '#64B5F6',
      values: [
        { key: 'MONTHLY', name: '月次セキュリティチェック' },
        { key: 'PC_PATCH', name: 'PCセキュリティパッチ適用確認' },
      ],
    },
  ];

  // クエリパラメータ由来の初期フィルタ（検索バーのチップ表示用）
  const initialFilters: Filter[] = useMemo(() => {
    const f: Filter[] = [];
    if (queryIncludeCompleted) {
      f.push({
        id: 'includeCompleted-init',
        key: 'includeCompleted',
        color: '#81C784',
        value: { key: 'true', name: '実施済みを含む' },
      });
    }
    if (queryType === 'MONTHLY' || queryType === 'PC_PATCH') {
      f.push({
        id: 'securityCheckType-init',
        key: 'securityCheckType',
        color: '#64B5F6',
        value: {
          key: queryType,
          name:
            queryType === 'MONTHLY' ? '月次セキュリティチェック' : 'PCセキュリティパッチ適用確認',
        },
      });
    }
    return f;
  }, [queryType, queryIncludeCompleted]);

  const listItems: ListPanelItem[] = items.map((item) => ({
    id: item.securityCheckResultId,
    primary: item.title,
    secondary: item.assessmentName
      ? `${item.assessmentName}  ${item.managementNumber1 || ''}`
      : undefined,
  }));

  const renderItem = (item: ListPanelItem, selected: boolean) => {
    const found = items.find((i) => i.securityCheckResultId === item.id);
    if (!found) return null;
    return <SecurityCheckListItem item={found} />;
  };

  // パスパラメータで直接詳細表示の場合はConditionListなし（別タブ想定・閉じる導線を表示）
  if (isDirectMode) {
    return (
      <>
        {selectedItem ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
            >
              <Button
                size="small"
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                onClick={() => window.close()}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                閉じる
              </Button>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DetailPanel>
                <SecurityCheckDetail detail={selectedItem} onExecuted={handleExecuted} />
              </DetailPanel>
            </Box>
          </Box>
        ) : (
          <></>
        )}
        <Circular open={isProgress} />
      </>
    );
  }

  return (
    <>
      <DomainBreadcrumbs
        items={[{ label: 'セキュリティ', to: '/security' }, { label: 'セキュリティチェック' }]}
      />
      <ConditionList
        conditions={conditions}
        items={listItems}
        initialFilters={initialFilters}
        selectedId={selectedItem?.securityCheckResultId}
        onSelect={handleSelectItem}
        onSearch={handleSearch}
        showCreateButton={false}
        renderItem={renderItem}
        isProgress={isProgress}
        conditionKey="security-check"
        userProfileKey="security-check-list-search"
      >
        {selectedItem ? (
          <SecurityCheckDetail detail={selectedItem} onExecuted={handleExecuted} />
        ) : null}
      </ConditionList>
    </>
  );
};

export default SecurityCheckPage;
